import { TableLoadingSpinner } from '../../../../design/components/LoadingSpinner';
import { isValidSortDirection } from '../../../pages/talent-directory/sort-option';
import { pageSizeOptions } from '../common/constants';
import { GeneralDataGrid } from '../common/GeneralDataGrid';

import type { SkillShort } from '../../../../../../shared/source/data-contract/skill';
import type { SortOption } from '../../../pages/talent-directory/sort-option';
import type {
    GridCallbackDetails,
    GridColDef,
    GridPaginationModel,
    GridSlots,
    GridValidRowModel,
} from '@mui/x-data-grid-pro';
import type { FunctionComponent } from 'react';

export type EmployeeRow = {
    readonly id: string | undefined;
    readonly emailAddress: string | undefined;
    readonly firstName: string | undefined;
    readonly lastName: string | undefined;
    readonly skills: readonly SkillShort[];
};

type EmployeesDataGridProps = {
    readonly checkboxSelection?: boolean;
    readonly columns: readonly GridColDef<EmployeeRow>[] | readonly GridColDef<GridValidRowModel>[];
    readonly components?: Partial<GridSlots> | undefined;
    readonly isFetching: boolean;
    readonly isPending: boolean;
    readonly noDataMessage: string;
    readonly onPaginationModelChange?: (model: GridPaginationModel, details: GridCallbackDetails) => void;
    readonly onRowClick?: (value: string) => void;
    readonly onRowSelectionModelChange?: (selectedRowIds: readonly string[]) => void;
    readonly onSortOptionsChange?: (options: readonly SortOption[]) => void;
    readonly paginationModel: GridPaginationModel;
    readonly rowCount: number;
    readonly rows: readonly EmployeeRow[];
    readonly sortOptions: readonly SortOption[];
};

export const EmployeesDataGrid: FunctionComponent<EmployeesDataGridProps> = ({
    checkboxSelection,
    columns,
    components,
    isFetching,
    isPending,
    noDataMessage,
    onPaginationModelChange,
    onRowClick,
    onRowSelectionModelChange,
    onSortOptionsChange,
    paginationModel,
    rowCount,
    rows,
    sortOptions,
}) => (
    <GeneralDataGrid
        checkboxSelection={checkboxSelection}
        columns={columns}
        disableRowSelectionOnClick={true}
        getRowHeight={() => 'auto'}
        loading={isPending || isFetching}
        localeText={{
            noRowsLabel: noDataMessage,
        }}
        pageSizeOptions={pageSizeOptions}
        pagination={true}
        paginationMode="server"
        paginationModel={paginationModel}
        rowCount={rowCount}
        rows={rows}
        slots={{
            loadingOverlay: TableLoadingSpinner,
            ...components,
        }}
        sortModel={sortOptions.map(({ direction, field }) => ({
            field,
            sort: direction,
        }))}
        sx={{
            '& .MuiDataGrid-cell': {
                alignItems: 'baseline',
                py: '8px',
            },
            height: '70vh',
        }}
        onPaginationModelChange={onPaginationModelChange}
        onRowClick={(row) => onRowClick?.(String(row.id))}
        onRowSelectionModelChange={(values) => onRowSelectionModelChange?.(Object.values(values).map(String))}
        onSortModelChange={(sortModel) =>
            onSortOptionsChange?.(
                sortModel.filter(isValidSortDirection).map(({ field, sort }) => ({ direction: sort, field })),
            )
        }
    />
);
